import { useEffect, useMemo, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Smartphone, HardDrive, BatteryFull, ShieldCheck, ArrowRight, ArrowLeft,
  Check, AlertTriangle, RotateCcw, Sparkles, Wrench, Loader2,
  Package, Star, ThumbsUp, MessageCircle, Palette, ImageOff, Minus, Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { getConfig, createQuote, buildWhatsAppUrl } from "@/lib/api";

const STEP_META = [
  { icon: Smartphone, label: "Modelo" },
  { icon: Palette, label: "Color" },
  { icon: HardDrive, label: "Capacidad" },
  { icon: BatteryFull, label: "Batería" },
  { icon: ShieldCheck, label: "Estado" },
  { icon: Sparkles, label: "Detalles" },
];
const TOTAL_STEPS = 6;
const VARIANT_ORDER = ["Normal", "Plus", "Pro", "Pro Max"];
const phoneSlug = (modelName) => modelName.replace(/\s+/g, "-").toLowerCase();
// WebP ligero para entrega rápida; el JPG original se mantiene como fuente/fallback.
const phoneImgFor = (modelName) => `/phones/${phoneSlug(modelName)}.webp`;
const phoneJpgFor = (modelName) => `/phones/${phoneSlug(modelName)}.jpg`;
// Modelos sin asset fotográfico real y verificado: se muestran como "Imagen pendiente".
const PENDING_IMAGES = new Set([]);

const fade = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -16 },
  transition: { duration: 0.28, ease: [0.16, 1, 0.3, 1] },
};

const variantOf = (name, gen) => {
  const base = `iPhone ${gen}`;
  if (name === base) return "Normal";
  return name.slice(base.length).trim();
};

export default function TradeIn() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0); // 0..4 wizard, 5 = resultado
  const [generation, setGeneration] = useState(null);

  const [model, setModel] = useState(null);
  const [capacity, setCapacity] = useState(null);
  const [color, setColor] = useState(null);
  const [battery, setBattery] = useState(87);
  const [cycles, setCycles] = useState("");
  const [problems, setProblems] = useState([]);
  const [aesthetic, setAesthetic] = useState(null);
  const [hasBox, setHasBox] = useState(false);
  const [operation, setOperation] = useState(null);
  const [simType, setSimType] = useState("");
  const modelHeadingRef = useRef(null);

  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getConfig()
      .then((c) => {
        setConfig(c);
        setGeneration((c.models || []).filter((m) => m.public !== false)[0]?.generation ?? null);
      })
      .catch(() => toast.error("No se pudo cargar el catálogo"))
      .finally(() => setLoading(false));
  }, []);

  // Modelos visibles al cliente: solo los que tienen precio de mercado real.
  // El iPhone 18 (y cualquier modelo nuevo sin precio) NO aparece en el cotizador público,
  // pero sigue disponible en el catálogo del Admin (inventario, combobox, banco de imágenes).
  const publicModels = useMemo(() => (config?.models || []).filter((m) => m.public !== false), [config]);

  const generations = useMemo(() => {
    if (!publicModels.length) return [];
    return [...new Set(publicModels.map((m) => m.generation))].sort((a, b) => a - b);
  }, [publicModels]);

  // URL de imagen para un modelo (custom del admin o WebP local).
  const imgUrlFor = (m, custom) => custom.has(m.name)
    ? `${process.env.REACT_APP_BACKEND_URL}/api/model-image/${encodeURIComponent(m.name)}`
    : phoneImgFor(m.name);

  // Precarga INMEDIATA de las variantes de la generación visible (siguiente paso probable).
  useEffect(() => {
    if (!config || generation == null) return;
    const custom = new Set(config.custom_image_models || []);
    config.models
      .filter((m) => m.generation === generation && !PENDING_IMAGES.has(m.name))
      .forEach((m) => { const img = new Image(); img.src = imgUrlFor(m, custom); });
    // eslint-disable-next-line
  }, [config, generation]);

  // Precarga ESCALONADA (en segundo plano, sin saturar) del resto de generaciones.
  useEffect(() => {
    if (!config) return;
    const custom = new Set(config.custom_image_models || []);
    const urls = config.models
      .filter((m) => !PENDING_IMAGES.has(m.name))
      .map((m) => imgUrlFor(m, custom));
    let i = 0, timer;
    const tick = () => { if (i >= urls.length) return; const img = new Image(); img.src = urls[i++]; timer = setTimeout(tick, 120); };
    const start = () => { timer = setTimeout(tick, 0); };
    const idle = window.requestIdleCallback ? window.requestIdleCallback(start, { timeout: 1500 }) : setTimeout(start, 600);
    return () => { if (timer) clearTimeout(timer); if (window.cancelIdleCallback && typeof idle === "number") window.cancelIdleCallback(idle); else clearTimeout(idle); };
    // eslint-disable-next-line
  }, [config]);

  // Auto-scroll suave hasta "Ahora cuéntanos qué iPhone tienes" al elegir modalidad (respeta movimiento reducido).
  useEffect(() => {
    if (!operation || step !== 0) return;
    const run = () => {
      const el = modelHeadingRef.current;
      if (!el) return;
      const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const y = el.getBoundingClientRect().top + window.scrollY - 96;
      window.scrollTo({ top: Math.max(0, y), behavior: reduce ? "auto" : "smooth" });
    };
    const raf = requestAnimationFrame(() => setTimeout(run, 60));
    return () => cancelAnimationFrame(raf);
  }, [operation, step]);

  const variants = useMemo(() => {
    if (!publicModels.length || generation == null) return [];
    return publicModels
      .filter((m) => m.generation === generation)
      .map((m) => ({ name: m.name, variant: variantOf(m.name, generation) }))
      .sort((a, b) => VARIANT_ORDER.indexOf(a.variant) - VARIANT_ORDER.indexOf(b.variant));
  }, [publicModels, generation]);

  const selectedModel = useMemo(
    () => config?.models.find((m) => m.name === model) || null,
    [config, model]
  );

  const isExcluding = useMemo(
    () => (config?.damages || []).some((d) => d.excluding && problems.includes(d.key)),
    [config, problems]
  );

  const toggleProblem = (d) => {
    setProblems((prev) => {
      if (d.key === "none") return prev.includes("none") ? [] : ["none"];
      const withoutNone = prev.filter((k) => k !== "none");
      return withoutNone.includes(d.key) ? withoutNone.filter((k) => k !== d.key) : [...withoutNone, d.key];
    });
  };

  const threshold = config?.battery_rule?.threshold ?? 85;
  const boxBonus = config?.box_bonus ?? 0;
  const batteryOk = battery >= threshold;

  const needsCycles = !!selectedModel && Number(selectedModel.generation) >= 15;
  const cyclesNum = cycles === "" ? null : Number(cycles);
  const cyclesValid = !needsCycles || (cycles !== "" && Number.isInteger(cyclesNum) && cyclesNum >= 0);

  const goNext = () => setStep((s) => s + 1);
  const goBack = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    setSubmitting(true);
    try {
      const q = await createQuote({
        model,
        capacity,
        color,
        battery_percent: battery,
        problems,
        aesthetic,
        has_box: hasBox,
        operation,
        charge_cycles: needsCycles ? cyclesNum : null,
        sim_type: simType || null,
      });
      setQuote(q);
      setStep(TOTAL_STEPS);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error("No se pudo generar la cotización");
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => {
    setModel(null); setCapacity(null); setColor(null); setBattery(87); setCycles("");
    setProblems([]); setAesthetic(null); setHasBox(false); setOperation(null); setSimType("");
    setQuote(null); setGeneration(generations[0] ?? null); setStep(0);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const canContinue = () => {
    if (step === 0) return !!operation && !!model;
    if (step === 1) return !!color;
    if (step === 2) return !!capacity;
    if (step === 3) return battery >= 1 && cyclesValid;
    if (step === 4) return problems.length > 0;
    if (step === 5) return !!aesthetic;
    return false;
  };

  const handleContinue = () => {
    if (step === 4) {
      if (isExcluding) return submit();
      return goNext();
    }
    if (step === 5) return submit();
    goNext();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  const progress = step >= TOTAL_STEPS ? 100 : ((step + 1) / TOTAL_STEPS) * 100;

  return (
    <div className="min-h-screen bg-white text-zinc-900">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-zinc-200/60">
        <div className="max-w-xl mx-auto px-5 py-3 relative flex items-center justify-center">
          <button onClick={restart} data-testid="brand-home-button" className="flex items-center">
            <img src="/puntocell-logo.png" alt="PuntoCell Phone Store" className="h-6 sm:h-7 w-auto" />
          </button>
          {step < TOTAL_STEPS && (
            <span className="absolute right-5 top-1/2 -translate-y-1/2 text-xs uppercase tracking-widest font-semibold text-zinc-400" data-testid="step-indicator">
              Paso {step + 1}/{TOTAL_STEPS}
            </span>
          )}
        </div>
        {step < TOTAL_STEPS && (
          <div className="max-w-xl mx-auto px-5 pb-3">
            <div className="h-1.5 w-full bg-zinc-100 rounded-full overflow-hidden">
              <motion.div className="h-full bg-zinc-900 rounded-full" animate={{ width: `${progress}%` }} transition={{ duration: 0.4, ease: "easeOut" }} />
            </div>
          </div>
        )}
      </header>

      <main className="max-w-xl mx-auto px-5 py-7 pb-32">
        <AnimatePresence mode="wait">
          {/* PASO 1 — MODELO + VARIANTE */}
          {step === 0 && (
            <motion.section key="s0" {...fade} data-testid="step-model">
              <StepHeading n={1} title="¿Qué quieres hacer con tu iPhone?" subtitle="Elige una modalidad para empezar." />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2" data-testid="operation-toggle">
                {[
                  { key: "trade_in", icon: "🔄", title: "Cambiar mi iPhone", desc: "Úsalo como parte de pago por otro equipo.", badge: "Recibes mayor valor", badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200" },
                  { key: "cash", icon: "💵", title: "Vender mi iPhone", desc: "Recibe el pago por tu equipo.", badge: "Valor en efectivo", badgeClass: "bg-zinc-100 text-zinc-600 border-zinc-200" },
                ].map((o) => {
                  const active = operation === o.key;
                  return (
                    <button key={o.key} data-testid={`operation-${o.key}`} onClick={() => setOperation(o.key)}
                      className={`text-left rounded-2xl border p-4 transition-all ${active ? "border-zinc-900 bg-zinc-900 text-white shadow-[0_4px_20px_rgba(0,0,0,0.12)]" : "border-zinc-200 bg-white text-zinc-900 hover:border-zinc-300"}`}>
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-2xl leading-none" aria-hidden>{o.icon}</span>
                        <span className={`text-[11px] font-semibold rounded-full border px-2 py-0.5 ${active ? "bg-white/15 text-white border-white/20" : o.badgeClass}`}>{o.badge}</span>
                      </div>
                      <p className="mt-2 text-base font-semibold leading-tight">{o.title}</p>
                      <p className={`mt-0.5 text-sm ${active ? "text-white/70" : "text-zinc-500"}`}>{o.desc}</p>
                      <span className={`mt-2 inline-flex items-center gap-1 text-xs font-semibold ${active ? "text-white" : "text-transparent"}`} data-testid={active ? `operation-selected-${o.key}` : undefined}>
                        <Check className="w-3.5 h-3.5" /> Seleccionado
                      </span>
                    </button>
                  );
                })}
              </div>

              {operation && (
                <motion.div {...fade} data-testid="model-section" className="mt-7">
                  <p ref={modelHeadingRef} className="text-base font-semibold text-zinc-900 mb-4 scroll-mt-28">Ahora cuéntanos qué iPhone tienes</p>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {generations.map((g) => (
                      <button
                        key={g}
                        data-testid={`generation-pill-${g}`}
                        onClick={() => { setGeneration(g); setModel(null); setCapacity(null); setColor(null); }}
                        className={`px-4 h-10 rounded-full text-sm font-medium transition-all ${
                          generation === g ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
                        }`}
                      >
                        iPhone {g}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs uppercase tracking-widest font-semibold text-zinc-400 mb-3">Variante</p>
              <div className="grid grid-cols-2 gap-3">
                {variants.map((v) => {
                  const active = model === v.name;
                  return (
                    <button
                      key={v.name}
                      data-testid={`variant-option-${v.name.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => { setModel(v.name); setCapacity(null); setColor(null); }}
                      className={`rounded-2xl border transition-all flex flex-col overflow-hidden ${
                        active ? "border-zinc-900 bg-zinc-50 shadow-[0_4px_20px_rgba(0,0,0,0.10)]" : "border-zinc-200 bg-white hover:border-zinc-300"
                      }`}
                    >
                      <div className="w-full h-24 sm:h-28 flex items-center justify-center overflow-hidden bg-white px-2 pt-3">
                        {PENDING_IMAGES.has(v.name) ? (
                          <div
                            data-testid={`variant-image-pending-${v.name.replace(/\s+/g, "-").toLowerCase()}`}
                            className="h-full w-full flex flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-zinc-200 text-zinc-300"
                          >
                            <ImageOff className="w-6 h-6" />
                            <span className="text-[10px] font-medium text-zinc-400">Imagen pendiente</span>
                          </div>
                        ) : (
                          <img
                            src={(config?.custom_image_models || []).includes(v.name)
                              ? `${process.env.REACT_APP_BACKEND_URL}/api/model-image/${encodeURIComponent(v.name)}`
                              : phoneImgFor(v.name)}
                            alt={v.name}
                            decoding="async"
                            data-fallback={(config?.custom_image_models || []).includes(v.name) ? "" : phoneJpgFor(v.name)}
                            onError={(e) => {
                              const fb = e.currentTarget.getAttribute("data-fallback");
                              if (fb && !e.currentTarget.src.endsWith(".jpg")) { e.currentTarget.src = fb; }
                              else { e.currentTarget.style.visibility = "hidden"; }
                            }}
                            className="h-full w-full object-contain"
                          />
                        )}
                      </div>
                      <div className="px-2 pt-2 pb-3 text-center border-t border-zinc-100">
                        <span className="block text-base font-semibold text-zinc-900 leading-tight">{v.variant}</span>
                        <span className="block text-xs text-zinc-400 mt-0.5">iPhone {generation}{v.variant === "Normal" ? "" : ` ${v.variant}`}</span>
                        <span className={`mt-1.5 inline-flex items-center gap-1 text-xs font-semibold ${active ? "text-zinc-900" : "text-transparent"}`} data-testid={active ? "variant-selected-badge" : undefined}>
                          <Check className="w-3.5 h-3.5" /> Seleccionado
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
                </motion.div>
              )}
            </motion.section>
          )}

          {/* PASO 2 — COLOR */}
          {step === 1 && selectedModel && (
            <motion.section key="s1c" {...fade} data-testid="step-color">
              <StepHeading n={2} title="¿De qué color es tu iPhone?" subtitle={selectedModel.name} />
              <div className="grid grid-cols-2 gap-3">
                {(selectedModel.colors || []).map((c) => {
                  const active = color === c.name;
                  return (
                    <button
                      key={c.name}
                      data-testid={`color-option-${c.name.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => setColor(c.name)}
                      className={`min-h-[60px] rounded-2xl border flex items-center gap-3 px-4 text-left transition-all ${
                        active ? "border-zinc-900 bg-zinc-900 text-white shadow-[0_4px_20px_rgba(0,0,0,0.12)]" : "border-zinc-200 bg-white hover:border-zinc-300"
                      }`}
                    >
                      <span className="w-7 h-7 rounded-full border border-zinc-300 shrink-0" style={{ backgroundColor: c.hex }} />
                      <span className="font-medium text-sm">{c.name}</span>
                      {active && <Check className="w-4 h-4 ml-auto shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </motion.section>
          )}

          {/* PASO 3 — CAPACIDAD */}
          {step === 2 && selectedModel && (
            <motion.section key="s1" {...fade} data-testid="step-capacity">
              <StepHeading n={3} title="¿Cuánto almacenamiento tiene?" subtitle={selectedModel.name} />
              <div className="grid grid-cols-2 gap-3">
                {selectedModel.capacities.map((c) => {
                  const active = capacity === c;
                  return (
                    <button
                      key={c}
                      data-testid={`capacity-option-${c.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => setCapacity(c)}
                      className={`min-h-[72px] rounded-2xl border font-semibold text-lg transition-all ${
                        active ? "border-zinc-900 bg-zinc-900 text-white shadow-[0_4px_20px_rgba(0,0,0,0.12)]" : "border-zinc-200 bg-white hover:border-zinc-300"
                      }`}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
            </motion.section>
          )}

          {/* PASO 4 — BATERÍA */}
          {step === 3 && (
            <motion.section key="s2" {...fade} data-testid="step-battery">
              <StepHeading n={4} title="¿Cómo está la batería?" subtitle="Lo encuentras en Ajustes › Batería › Salud." />
              <div className="rounded-2xl border border-zinc-200 p-7 flex flex-col items-center">
                <div className="flex items-center justify-center gap-5 sm:gap-7">
                  <button
                    type="button"
                    data-testid="battery-minus"
                    aria-label="Reducir 1%"
                    onClick={() => setBattery((b) => Math.max(1, b - 1))}
                    disabled={battery <= 1}
                    className="h-14 w-14 shrink-0 grid place-items-center rounded-full border border-zinc-200 text-zinc-700 transition-colors hover:bg-zinc-50 active:bg-zinc-100 disabled:opacity-40 disabled:pointer-events-none"
                  >
                    <Minus className="h-6 w-6" strokeWidth={2.5} />
                  </button>
                  <div className="text-6xl font-semibold tracking-tight tabular-nums min-w-[3.5ch] text-center" data-testid="battery-value">
                    {battery}<span className="text-2xl text-zinc-400">%</span>
                  </div>
                  <button
                    type="button"
                    data-testid="battery-plus"
                    aria-label="Aumentar 1%"
                    onClick={() => setBattery((b) => Math.min(100, b + 1))}
                    disabled={battery >= 100}
                    className="h-14 w-14 shrink-0 grid place-items-center rounded-full border border-zinc-200 text-zinc-700 transition-colors hover:bg-zinc-50 active:bg-zinc-100 disabled:opacity-40 disabled:pointer-events-none"
                  >
                    <Plus className="h-6 w-6" strokeWidth={2.5} />
                  </button>
                </div>
                <div className={`mt-3 px-3 h-8 grid place-items-center rounded-full text-sm font-medium ${batteryOk ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`} data-testid="battery-level-pill">
                  {batteryOk ? `Batería óptima (≥ ${threshold}%)` : `Batería baja (< ${threshold}%)`}
                </div>
                <div className="w-full mt-7 px-1">
                  <Slider data-testid="battery-slider" value={[battery]} min={1} max={100} step={1} onValueChange={(v) => setBattery(v[0])} />
                  <div className="flex justify-between text-xs text-zinc-400 mt-2"><span>1%</span><span>100%</span></div>
                </div>
              </div>
              {needsCycles && (
                <div className="mt-4 rounded-2xl border border-zinc-200 p-6" data-testid="cycles-field">
                  <label className="block text-sm font-medium text-zinc-700 mb-1">Ciclos de carga <span className="text-red-500">*</span></label>
                  <p className="text-xs text-zinc-400 mb-3">Ajustes › Batería › Salud de la batería › Recuento de ciclos. Obligatorio para iPhone 15, 16 y 17.</p>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    inputMode="numeric"
                    data-testid="cycles-input"
                    placeholder="Ej: 320"
                    value={cycles}
                    onChange={(e) => setCycles(e.target.value.replace(/[^0-9]/g, ""))}
                    className="h-12 rounded-xl text-lg"
                  />
                  {cycles === "" && (
                    <p className="text-xs text-amber-600 mt-2" data-testid="cycles-hint">Ingresa los ciclos de carga para continuar.</p>
                  )}
                </div>
              )}
            </motion.section>
          )}

          {/* PASO 5 — ESTADO FÍSICO (DAÑOS) */}
          {step === 4 && (
            <motion.section key="s3" {...fade} data-testid="step-damage">
              <StepHeading n={5} title="¿El equipo tiene alguno de estos daños?" subtitle="Puedes seleccionar varios si aplica." />
              <div className="grid grid-cols-1 gap-2.5">
                {config.damages.map((d) => {
                  const active = problems.includes(d.key);
                  return (
                    <button
                      key={d.key}
                      data-testid={`damage-option-${d.key}`}
                      onClick={() => toggleProblem(d)}
                      className={`w-full flex items-center justify-between min-h-[56px] px-5 rounded-2xl border text-left transition-all ${
                        active ? (d.excluding ? "border-amber-500 bg-amber-50" : "border-zinc-900 bg-zinc-900 text-white") : "border-zinc-200 bg-white hover:border-zinc-300"
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        {d.excluding
                          ? <AlertTriangle className={`w-5 h-5 ${active ? "text-amber-500" : "text-zinc-300"}`} />
                          : <ShieldCheck className={`w-5 h-5 ${active ? "text-white" : "text-emerald-500"}`} />}
                        <span className="font-medium">{d.label}</span>
                      </span>
                      {active && <Check className={`w-5 h-5 ${d.excluding ? "text-amber-500" : "text-white"}`} />}
                    </button>
                  );
                })}
              </div>
              {isExcluding && (
                <div className="mt-4 rounded-2xl bg-amber-50 border border-amber-200 p-4" data-testid="damage-warning">
                  <p className="font-semibold text-amber-800 text-sm">Este equipo requiere evaluación técnica</p>
                  <p className="text-sm text-amber-700 mt-1">Por el/los daño(s) indicado(s), no podemos ofrecer una cotización automática. Un técnico debe evaluar el equipo para determinar si podemos comprarlo.</p>
                </div>
              )}
            </motion.section>
          )}

          {/* PASO 6 — DETALLES (ESTÉTICA + CAJA) */}
          {step === 5 && (
            <motion.section key="s4" {...fade} data-testid="step-details">
              <StepHeading n={6} title="Últimos detalles" subtitle="Un par de datos para afinar tu estimación." />

              <p className="text-sm font-medium text-zinc-800 mb-3">¿Cómo está estéticamente tu iPhone?</p>
              <div className="grid grid-cols-1 gap-3 mb-8">
                {config.aesthetic_conditions.map((a) => {
                  const active = aesthetic === a.key;
                  const Icon = a.key === "excellent" ? Star : ThumbsUp;
                  return (
                    <button
                      key={a.key}
                      data-testid={`aesthetic-option-${a.key}`}
                      onClick={() => setAesthetic(a.key)}
                      className={`w-full flex items-start gap-3 min-h-[64px] px-5 py-3.5 rounded-2xl border text-left transition-all ${
                        active ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 bg-white hover:border-zinc-300"
                      }`}
                    >
                      <Icon className={`w-5 h-5 mt-0.5 ${active ? "text-white" : "text-zinc-400"}`} />
                      <span>
                        <span className="block font-medium">{a.label}</span>
                        <span className={`block text-sm ${active ? "text-zinc-300" : "text-zinc-500"}`}>{a.description}</span>
                      </span>
                      {active && <Check className="w-5 h-5 ml-auto shrink-0" />}
                    </button>
                  );
                })}
              </div>

              <p className="text-sm font-medium text-zinc-800 mb-3">¿Tienes la caja original?</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  data-testid="box-option-yes"
                  onClick={() => setHasBox(true)}
                  className={`min-h-[64px] rounded-2xl border font-medium transition-all flex flex-col items-center justify-center gap-0.5 ${
                    hasBox ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 bg-white hover:border-zinc-300"
                  }`}
                >
                  <span className="flex items-center gap-2"><Package className="w-4 h-4" /> Sí</span>
                  <span className={`text-xs ${hasBox ? "text-emerald-300" : "text-emerald-500"}`}>+${boxBonus}</span>
                </button>
                <button
                  data-testid="box-option-no"
                  onClick={() => setHasBox(false)}
                  className={`min-h-[64px] rounded-2xl border font-medium transition-all flex flex-col items-center justify-center gap-0.5 ${
                    !hasBox ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 bg-white hover:border-zinc-300"
                  }`}
                >
                  <span>No</span>
                  <span className="text-xs text-zinc-400">Sin bonificación</span>
                </button>
              </div>

              <p className="text-sm font-medium text-zinc-800 mb-3 mt-8">Tipo de SIM <span className="text-zinc-400 font-normal">(opcional)</span></p>
              <div className="grid grid-cols-2 gap-3" data-testid="sim-type-options">
                {(config.sim_types || []).map((s) => {
                  const active = simType === s.label;
                  return (
                    <button
                      key={s.key}
                      data-testid={`sim-type-option-${s.key}`}
                      onClick={() => setSimType(active ? "" : s.label)}
                      className={`min-h-[56px] rounded-2xl border font-medium text-sm px-3 transition-all flex items-center justify-center text-center ${
                        active ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 bg-white hover:border-zinc-300"
                      }`}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </motion.section>
          )}

          {/* RESULTADO */}
          {step === TOTAL_STEPS && quote && (
            <motion.section key="s5" {...fade}>
              {quote.requires_evaluation ? (
                <TechnicalEvaluation quote={quote} whatsapp={config.whatsapp_number} onRestart={restart} onBack={() => setStep(4)} />
              ) : quote.price_available === false ? (
                <PriceUnavailable quote={quote} whatsapp={config.whatsapp_number} onRestart={restart} onBack={() => setStep(4)} />
              ) : (
                <ResultQuote quote={quote} whatsapp={config.whatsapp_number} onRestart={restart} config={config} deliveredGen={selectedModel?.generation ?? null} />
              )}
            </motion.section>
          )}
        </AnimatePresence>
      </main>

      {step < TOTAL_STEPS && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-xl border-t border-zinc-200/60">
          <div className="max-w-xl mx-auto px-5 py-4 flex items-center gap-3">
            {step > 0 && (
              <Button data-testid="back-button" variant="outline" onClick={goBack} className="h-14 rounded-2xl px-5 border-zinc-200">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            )}
            <Button
              data-testid="continue-button"
              disabled={!canContinue() || submitting}
              onClick={handleContinue}
              className="flex-1 h-14 rounded-2xl text-base font-semibold bg-zinc-900 hover:bg-zinc-800"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" />
                : step === 5 ? <>Ver mi estimación <Sparkles className="w-5 h-5 ml-1" /></>
                : (step === 4 && isExcluding) ? <>Continuar con evaluación técnica <ArrowRight className="w-5 h-5 ml-1" /></>
                : <>Continuar <ArrowRight className="w-5 h-5 ml-1" /></>}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function StepHeading({ n, title, subtitle }) {
  const Icon = STEP_META[n - 1].icon;
  return (
    <div className="mb-6">
      <div className="w-11 h-11 rounded-2xl bg-zinc-100 grid place-items-center mb-4">
        <Icon className="w-5 h-5 text-zinc-700" />
      </div>
      <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight leading-tight">{title}</h1>
      {subtitle && <p className="text-sm sm:text-base text-zinc-500 mt-1.5">{subtitle}</p>}
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-zinc-100 last:border-0">
      <span className="text-sm text-zinc-500">{label}</span>
      <span className="text-sm font-medium text-zinc-900 text-right">{value}</span>
    </div>
  );
}

function WhatsAppButton({ whatsapp, quote, label, testid, extraLines }) {
  const send = () => window.open(buildWhatsAppUrl(whatsapp, quote, extraLines), "_blank");
  return (
    <Button
      data-testid={testid}
      onClick={send}
      className="w-full h-14 rounded-2xl text-base font-semibold bg-emerald-500 hover:bg-emerald-600 text-white"
    >
      <MessageCircle className="w-5 h-5 mr-1" /> {label}
    </Button>
  );
}

const PAY_METHODS = ["Efectivo", "Zelle", "USDT", "Pago móvil (bolívares)"];

function PreferenceSection({ quote, config, deliveredGen, pref, setPref }) {
  const isCash = quote.operation === "cash";

  const targetLines = useMemo(() => {
    if (isCash || !config || deliveredGen == null) return [];
    const byGen = {};
    (config.models || []).forEach((m) => { (byGen[m.generation] = byGen[m.generation] || []).push(m); });
    return Object.keys(byGen).map(Number).filter((g) => g > deliveredGen).sort((a, b) => a - b).map((g) => {
      const vs = byGen[g].map((m) => variantOf(m.name, g)).sort((a, b) => VARIANT_ORDER.indexOf(a) - VARIANT_ORDER.indexOf(b));
      const labels = vs.map((v) => (v === "Normal" ? String(g) : `${g} ${v}`));
      return { gen: g, title: `iPhone línea ${g}`, variantsText: labels.join(" / ") };
    });
  }, [isCash, config, deliveredGen]);

  if (isCash) {
    return (
      <section className="mt-8" data-testid="preference-section-cash">
        <h2 className="text-lg font-semibold text-zinc-900">¿Cómo prefieres recibir tu dinero?</h2>
        <p className="text-sm text-zinc-500 mt-0.5 mb-4">Elige tu método preferido.</p>
        <div className="grid grid-cols-2 gap-3">
          {PAY_METHODS.map((m) => {
            const active = pref?.value === m;
            return (
              <button key={m} data-testid={`pay-method-${m.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
                onClick={() => setPref(active ? null : { type: "method", value: m, label: m })}
                className={`min-h-[56px] rounded-2xl border px-4 font-medium text-sm transition-all flex items-center justify-between ${active ? "border-zinc-900 bg-zinc-900 text-white shadow-[0_4px_20px_rgba(0,0,0,0.12)]" : "border-zinc-200 bg-white hover:border-zinc-300"}`}>
                {m}{active && <Check className="w-4 h-4 ml-2 shrink-0" />}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-zinc-400 mt-3">Método sujeto a confirmación por PuntoCell.</p>
      </section>
    );
  }

  return (
    <section className="mt-8" data-testid="preference-section-trade">
      <h2 className="text-lg font-semibold text-zinc-900">¿Qué iPhone te gustaría llevarte?</h2>
      <p className="text-sm text-zinc-500 mt-0.5 mb-4">Elige la línea que te interesa o descubre tus opciones.</p>
      {targetLines.length === 0 ? (
        <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-4" data-testid="preference-no-superior">
          <p className="text-sm text-zinc-600">Ya tienes una de las generaciones más recientes. Un asesor de PuntoCell puede mostrarte las opciones de cambio disponibles.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {targetLines.map((l) => {
            const active = pref?.type === "line" && pref?.value === l.gen;
            return (
              <button key={l.gen} data-testid={`trade-line-${l.gen}`}
                onClick={() => setPref(active ? null : { type: "line", value: l.gen, label: l.title, variantsText: l.variantsText })}
                className={`text-left rounded-2xl border p-4 transition-all ${active ? "border-zinc-900 bg-zinc-900 text-white shadow-[0_4px_20px_rgba(0,0,0,0.12)]" : "border-zinc-200 bg-white hover:border-zinc-300"}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-base font-semibold">{l.title}</span>
                  {active && <Check className="w-4 h-4 shrink-0" />}
                </div>
                <span className={`block text-sm mt-0.5 ${active ? "text-white/70" : "text-zinc-500"}`}>{l.variantsText}</span>
              </button>
            );
          })}
          <button data-testid="trade-line-all"
            onClick={() => setPref(pref?.type === "all" ? null : { type: "all", value: "all", label: "Todas las opciones" })}
            className={`text-left rounded-2xl border p-4 transition-all ${pref?.type === "all" ? "border-zinc-900 bg-zinc-900 text-white shadow-[0_4px_20px_rgba(0,0,0,0.12)]" : "border-zinc-200 bg-white hover:border-zinc-300"}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-base font-semibold">Quiero ver todas las opciones</span>
              {pref?.type === "all" && <Check className="w-4 h-4 shrink-0" />}
            </div>
            <span className={`block text-sm mt-0.5 ${pref?.type === "all" ? "text-white/70" : "text-zinc-500"}`}>Quiero asesoría sobre alternativas (solo generaciones superiores a la que entrego).</span>
          </button>
        </div>
      )}
    </section>
  );
}

function prefToLines(pref, isCash) {
  if (!pref) return [];
  if (isCash) return pref.type === "method" ? ["💳 MÉTODO DE PAGO PREFERIDO:", pref.label] : [];
  if (pref.type === "line") return ["🔄 QUIERO CAMBIAR POR:", `${pref.label} (${pref.variantsText})`];
  if (pref.type === "all") return ["🔄 ME INTERESA:", "Conocer las opciones de cambio disponibles en generaciones superiores a mi iPhone actual."];
  return [];
}

function ResultQuote({ quote, whatsapp, onRestart, config, deliveredGen }) {
  const [pref, setPref] = useState(null);
  const extraLines = prefToLines(pref, quote.operation === "cash");
  return (
    <div className="pc-pop" data-testid="result-quote">
      <div className="text-center mb-6">
        <span className="inline-block text-xs uppercase tracking-widest font-semibold text-zinc-400">{quote.operation === "cash" ? "Venta en efectivo" : "Estimación de Trade-In"}</span>
      </div>

      <div className="rounded-3xl border border-zinc-200 overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.05)]">
        <div className="px-6 py-5">
          <SummaryRow label="Equipo" value={quote.model} />
          <SummaryRow label="Capacidad" value={quote.capacity} />
          {quote.color && <SummaryRow label="Color" value={quote.color} />}
          <SummaryRow label="Batería" value={`${quote.battery_percent}%`} />
          {quote.charge_cycles != null && <SummaryRow label="Ciclos de carga" value={String(quote.charge_cycles)} />}
          <SummaryRow label="Condición" value={quote.aesthetic_label} />
          <SummaryRow label="Caja original" value={quote.has_box ? `Sí (+$${quote.box_bonus})` : "No"} />
          {quote.sim_type && <SummaryRow label="Tipo de SIM" value={quote.sim_type} />}
          <SummaryRow label="Modalidad" value={quote.operation === "cash" ? "Venta en efectivo" : "Cambio / Trade-In"} />
          <SummaryRow label="Cotización" value={quote.id} />
        </div>
        <div className="bg-gradient-to-b from-emerald-50 to-white px-6 pt-6 pb-7 text-center border-t border-zinc-100">
          {quote.operation === "cash" ? (
            <>
              <p className="text-sm text-zinc-500">Valor de compra en efectivo</p>
              <div className="text-6xl sm:text-7xl font-semibold tracking-tight mt-2 tabular-nums" data-testid="result-price">${Number(quote.cash_price).toFixed(0)}</div>
              <p className="text-xs text-zinc-400 mt-3">Venta directa en efectivo: ${Number(quote.cash_discount_used).toFixed(2)} menos que el Trade-In (${Number(quote.price).toFixed(0)}).</p>
            </>
          ) : (
            <>
              <p className="text-sm text-zinc-500">Precio estimado de retoma</p>
              <div className="text-6xl sm:text-7xl font-semibold tracking-tight mt-2 tabular-nums" data-testid="result-price">${Number(quote.price).toFixed(0)}</div>
              <p className="text-xs text-zinc-400 mt-3">*Precio sujeto a revisión física del equipo.</p>
            </>
          )}
        </div>
      </div>

      <PreferenceSection quote={quote} config={config} deliveredGen={deliveredGen} pref={pref} setPref={setPref} />

      <div className="mt-8">
        <WhatsAppButton whatsapp={whatsapp} quote={quote} extraLines={extraLines} label="Enviar cotización a PuntoCell" testid="whatsapp-button" />
      </div>

      <button data-testid="restart-button" onClick={onRestart} className="w-full mt-4 h-14 rounded-2xl text-zinc-600 font-medium flex items-center justify-center gap-2 hover:bg-zinc-50 transition-colors">
        <RotateCcw className="w-4 h-4" /> Calcular otro equipo
      </button>
    </div>
  );
}

function PriceUnavailable({ quote, whatsapp, onRestart, onBack }) {
  return (
    <div className="pc-pop" data-testid="result-unavailable">
      <div className="rounded-3xl border border-zinc-200 overflow-hidden">
        <div className="bg-gradient-to-b from-zinc-50 to-white px-6 pt-8 pb-7 text-center">
          <div className="w-14 h-14 rounded-2xl bg-zinc-100 grid place-items-center mx-auto mb-4">
            <MessageCircle className="w-6 h-6 text-zinc-600" />
          </div>
          <span className="inline-block text-xs uppercase tracking-widest font-semibold text-zinc-400">Cotización</span>
          <h1 className="text-2xl font-semibold tracking-tight mt-2">Precio no disponible por ahora</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-sm mx-auto">
            Aún no tenemos publicado el valor de retoma para este equipo. Escríbenos por WhatsApp y te damos el precio al momento.
          </p>
        </div>
        <div className="px-6 py-5">
          <SummaryRow label="Equipo" value={quote.model} />
          <SummaryRow label="Capacidad" value={quote.capacity} />
          {quote.color && <SummaryRow label="Color" value={quote.color} />}
          <SummaryRow label="Batería" value={`${quote.battery_percent}%`} />
          {quote.charge_cycles != null && <SummaryRow label="Ciclos de carga" value={String(quote.charge_cycles)} />}
          {quote.sim_type && <SummaryRow label="Tipo de SIM" value={quote.sim_type} />}
          <SummaryRow label="Solicitud" value={quote.id} />
        </div>
      </div>
      <div className="mt-8">
        <WhatsAppButton whatsapp={whatsapp} quote={quote} label="Consultar precio por WhatsApp" testid="whatsapp-button" />
      </div>
      <button data-testid="review-button" onClick={onBack} className="w-full mt-4 h-14 rounded-2xl text-zinc-600 font-medium flex items-center justify-center gap-2 hover:bg-zinc-50 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Revisar datos ingresados
      </button>
      <button data-testid="restart-button" onClick={onRestart} className="w-full mt-1 h-12 rounded-2xl text-zinc-400 text-sm font-medium flex items-center justify-center gap-2 hover:bg-zinc-50 transition-colors">
        <RotateCcw className="w-4 h-4" /> Calcular otro equipo
      </button>
    </div>
  );
}

function TechnicalEvaluation({ quote, whatsapp, onRestart, onBack }) {  return (
    <div className="pc-pop" data-testid="result-technical">
      <div className="rounded-3xl border border-amber-200 overflow-hidden">
        <div className="bg-gradient-to-b from-amber-50 to-white px-6 pt-8 pb-7 text-center">
          <div className="w-14 h-14 rounded-2xl bg-amber-100 grid place-items-center mx-auto mb-4">
            <Wrench className="w-6 h-6 text-amber-600" />
          </div>
          <span className="inline-block text-xs uppercase tracking-widest font-semibold text-amber-500">Evaluación técnica</span>
          <h1 className="text-2xl font-semibold tracking-tight mt-2">Este equipo requiere evaluación técnica</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-sm mx-auto">
            Por el tipo de daño indicado, no podemos ofrecer una cotización automática. Un técnico debe evaluar el equipo para determinar si podemos comprarlo.
          </p>
        </div>
        <div className="px-6 py-5">
          <SummaryRow label="Equipo" value={quote.model} />
          <SummaryRow label="Capacidad" value={quote.capacity} />
          {quote.color && <SummaryRow label="Color" value={quote.color} />}
          <SummaryRow label="Batería" value={`${quote.battery_percent}%`} />
          {quote.charge_cycles != null && <SummaryRow label="Ciclos de carga" value={String(quote.charge_cycles)} />}
          <SummaryRow label={quote.problem_labels && quote.problem_labels.length > 1 ? "Daños" : "Daño"} value={quote.problem_label} />
          {quote.sim_type && <SummaryRow label="Tipo de SIM" value={quote.sim_type} />}
          <SummaryRow label="Solicitud" value={quote.id} />
        </div>
      </div>

      <div className="mt-8">
        <WhatsAppButton whatsapp={whatsapp} quote={quote} label="Continuar con evaluación técnica" testid="whatsapp-button" />
      </div>

      <button data-testid="review-button" onClick={onBack} className="w-full mt-4 h-14 rounded-2xl text-zinc-600 font-medium flex items-center justify-center gap-2 hover:bg-zinc-50 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Revisar datos ingresados
      </button>
      <button data-testid="restart-button" onClick={onRestart} className="w-full mt-1 h-12 rounded-2xl text-zinc-400 text-sm font-medium flex items-center justify-center gap-2 hover:bg-zinc-50 transition-colors">
        <RotateCcw className="w-4 h-4" /> Calcular otro equipo
      </button>
    </div>
  );
}
